import type React from "react"
import type { IParcel } from "@/types/explore"

export interface TourStep {
  target: string
  title: string
  body: string
  onEnter?: () => void
  onExit?: () => void
}

const DEMO_PARCEL: IParcel = {
  id: "75101000AJ0001",
  properties: {
    idu: "75101000AJ0001",
    section: "AJ",
    numero: "0001",
    code_dep: "75",
    nom_com: "Paris",
    code_com: "75056",
    com_abs: "000",
    code_arr: "751",
    feuille: 1,
    code_insee: "75056",
    contenance: 14898,
  },
  geometry: {
    type: "MultiPolygon",
    coordinates: [
      [
        [
          [2.337, 48.859],
          [2.34, 48.859],
          [2.34, 48.861],
          [2.337, 48.861],
          [2.337, 48.859],
        ],
      ],
    ],
  },
  bbox: [2.337, 48.859, 2.34, 48.861],
  center: [2.3385524841827503, 48.860369126094625],
  address: "1a Rue de l'Amiral de Coligny",
}

export const buildTourSteps = (options: {
  setLayersOpen: (open: boolean) => void
  setSelectedParcels: (parcels: IParcel[]) => void
  setParcelPOI: (poi: { lon: number | null; lat: number | null }) => void
  selectedParcelsRef: React.RefObject<IParcel[]>
}): TourStep[] => {
  let savedParcels: IParcel[] = []
  let savedPOI: { lon: number | null; lat: number | null } = { lon: null, lat: null }

  const root = () => document.getElementById("explore-map-v2-root")

  return [
    // 1 — Commune selector
    {
      target: '[data-tour-step="commune-selector"]',
      title: "Sélectionnez une commune",
      body: "Choisissez votre commune de travail. Raccourci : C",
    },
    // 2 — Address search
    {
      target: '[data-tour-step="address-search"]',
      title: "Recherchez une adresse",
      body: "Tapez une adresse pour centrer la carte. Raccourci : /",
    },
    // 3 — Parcel search: Reference
    {
      target: '[data-tour-step="parcel-search-reference"]',
      title: "Recherche par référence",
      body: "Cherchez une parcelle par sa référence cadastrale (ex : AB 0052). Raccourci : P",
      onEnter: () => {
        ;(document.querySelector('[data-tour-step="parcel-search-reference"]') as HTMLElement)?.click()
      },
    },
    // 4 — Parcel search: Carte
    {
      target: '[data-tour-step="parcel-search-carte"]',
      title: "Recherche par carte",
      body: "Filtrez les parcelles par surface, type de propriétaire ou SIREN.",
      onEnter: () => {
        ;(document.querySelector('[data-tour-step="parcel-search-carte"]') as HTMLElement)?.click()
      },
      onExit: () => {
        ;(document.querySelector('[data-tour-step="parcel-search-reference"]') as HTMLElement)?.click()
      },
    },
    // 5 — Parcel details (demo parcel)
    {
      target: '[data-tour-step="parcel-card"]',
      title: "Consultez les informations",
      body: "Chaque section (Propriétaire, PLU, Urbanisme, Taxes...) se déplie pour révéler les données détaillées.",
      onEnter: () => {
        savedParcels = [...(options.selectedParcelsRef.current ?? [])]
        savedPOI = { lon: null, lat: null }
        options.setSelectedParcels([DEMO_PARCEL])
        options.setParcelPOI({ lon: 2.3385, lat: 48.8604 })
      },
      onExit: () => {
        options.setSelectedParcels(savedParcels)
        options.setParcelPOI(savedPOI)
      },
    },
    // 6 — Recent parcels
    {
      target: '[data-tour-step="recent-parcels"]',
      title: "Parcelles récentes",
      body: "Retrouvez les dernières parcelles consultées pour y revenir en un clic.",
      onEnter: () => {
        options.setSelectedParcels([])
        options.setParcelPOI({ lon: null, lat: null })
      },
    },
    // 7 — Quick layers (collapsed panel)
    {
      target: '[data-tour-step="quick-layers"]',
      title: "Calques rapides",
      body: "Utilisez les raccourcis 1-9 pour activer ou désactiver vos calques favoris sans ouvrir le panneau.",
    },
    // 8 — Layers panel (expanded)
    {
      target: '[data-tour-step="layers-panel"]',
      title: "Explorez tous les calques",
      body: "Plus de 40 couches : urbanisme, risques, bâtiment, transports. Raccourci : L",
      onEnter: () => options.setLayersOpen(true),
      onExit: () => options.setLayersOpen(false),
    },
    // 9 — Layer search
    {
      target: '[data-tour-step="layer-search"]',
      title: "Recherchez un calque",
      body: "Tapez un mot-clé pour filtrer rapidement les calques disponibles.",
      onEnter: () => options.setLayersOpen(true),
    },
    // 10 — Presets
    {
      target: '[data-tour-step="presets-menu"]',
      title: "Sauvegardez vos calques",
      body: "Enregistrez et rechargez vos combinaisons de calques favorites.",
    },
    // 11 — Quick layer star
    {
      target: '[data-tour-step="layer-quick-star"]',
      title: "Ajoutez aux calques rapides",
      body: "Cliquez sur l'étoile pour ajouter un calque au panneau rapide (raccourcis 1-9).",
      onEnter: () => {
        root()?.classList.add("tour-show-stars")
      },
      onExit: () => {
        root()?.classList.remove("tour-show-stars")
      },
    },
    // 12 — Tools bar
    {
      target: '[data-tour-step="tools-bar"]',
      title: "Mesurez et étudiez",
      body: "Distance, surface, altimétrie et étude de constructibilité.",
      onEnter: () => options.setLayersOpen(false),
    },
    // 13 — Map style
    {
      target: '[data-tour-step="map-style"]',
      title: "Changez de fond de carte",
      body: "Plan, Satellite, Cadastre, 3D ou Street View.",
    },
    // 14 — Help button
    {
      target: '[data-tour-step="help-button"]',
      title: "Besoin d'aide ?",
      body: "Appuyez sur ? pour les raccourcis clavier, ou relancez ce guide ici.",
    },
  ]
}
